import { observable, makeObservable, action, runInAction } from "mobx";
import { GET_ALL_ENUMERATIONS } from "src/api/enumerations";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { RootStore } from "src/stores/RootStore";

export enum ENUM_TYPE {
    TENANCYPERSON_ROLES = 11,
    TENANCY_TYPES = 12,
    UTILIZATION = 13,
    MAINTENANCE_STATUS = 14,
    REALESTATEPERSON_ROLES = 15,
    UNIT_CATEGORIES = 17,
    USER_ROLES = 91,
    QUORUMS = 201,
    TILE_TYPES = 304,
    ACCOUNTLIST_TYPES = 310,
    ASSEMBLY_STATES = 401,
    ASSEMBLYUSERPRESENCE = 404,
    ROOM_TYPES = 503,
    APPLIANCE_TYPES = 504,
    PRIMARYCONTACT_TYPES = 559,
    NEWS_STATES = 601,
    RENTAL_STATES = 701,
    COSTTYPES = 902
}

export interface EnumItem {
    id: string;
    key: number;
    label: string;
    type: number;
}

const EMPTY_ITEMS: EnumItem[] = [];

export class EnumerationStore {
    rootStore: RootStore;

    isLoaded = false;
    enumerationsByType: Map<number, EnumItem[]> = new Map();
    language: string | undefined = undefined;

    // Caches the Map<key, {label}> built by getEnumMapByType for each source
    // array, so repeated calls during rendering return a stable reference.
    // Keyed on the array reference — naturally invalidated when loadAllEnums
    // replaces enumerationsByType with freshly-built arrays.
    private mapCache: WeakMap<EnumItem[], Map<number, { label: string }>> = new WeakMap();

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isLoaded: observable,
            enumerationsByType: observable,

            loadAllEnums: action
        });
    }

    loadAllEnums = async (language = "de") => {
        this.language = language;
        try {
            const types = Object.values(ENUM_TYPE).filter((v): v is number => typeof v === "number");
            const { data } = await apolloClientInstance.query({
                query: GET_ALL_ENUMERATIONS,
                variables: { language, types }
            });
            runInAction(() => {
                if (this.language !== language) {
                    return;
                }
                const map = new Map<number, EnumItem[]>();
                for (const item of data.common_enumerations) {
                    // The GraphQL scalars allow null for key/label/type; skip
                    // incomplete rows here so the EnumItem interface reflects
                    // what consumers actually get.
                    if (item.key == null || item.label == null || item.type == null) continue;
                    if (!map.has(item.type)) map.set(item.type, []);
                    map.get(item.type)!.push({
                        id: String(item.id),
                        key: item.key,
                        label: item.label,
                        type: item.type
                    });
                }
                this.enumerationsByType = map;
                this.isLoaded = true;
            });
        } catch (error) {
            // Keep isLoaded = false so callers can detect the failure and
            // (optionally) retry. We intentionally don't surface a toast —
            // the load runs right after login and a failure here should not
            // interrupt the login flow.
            console.error("Failed to load enumerations", error);
        }
    };

    ensureLanguage = async (language: string) => {
        if (this.language !== language) {
            await this.loadAllEnums(language);
        }
    };

    getEnumByType(type: number): EnumItem[] {
        return this.enumerationsByType.get(type) ?? EMPTY_ITEMS;
    }

    getEnumMapByType(type: number): Map<number, { label: string }> {
        const items = this.getEnumByType(type);
        const cached = this.mapCache.get(items);
        if (cached) return cached;

        const map = new Map<number, { label: string }>();
        for (const item of items) {
            map.set(item.key, { label: item.label });
        }
        this.mapCache.set(items, map);
        return map;
    }
}
